import {
  exactCallThreadId,
  normalizeThreads,
  validThread,
  type Conversation,
  type ThreadSummary,
} from './conversations-model'
import {
  errorKind,
  THREAD_RESOLUTION_QUERY_SIZE,
  type ConversationRepository,
} from './conversations-repository'

export type ThreadResolution =
  | {
      status: 'resolved'
      threadId: string
      basis: 'exact' | 'producer-rule'
      channel: ThreadSummary['channel']
    }
  | { status: 'choice-required'; candidates: ThreadSummary[] }
  | { status: 'empty' }
  | { status: 'error'; kind: 'forbidden' | 'transport' }

async function validateThread(
  repository: ConversationRepository,
  tenantId: string,
  patientId: string,
  threadId: string
) {
  const value = await repository.getExactThread(tenantId, patientId, threadId)
  if (!validThread(value)) return null
  const normalized = normalizeThreads({ [threadId]: value })
  return normalized[0] ?? null
}

async function recentChoice(
  repository: ConversationRepository,
  tenantId: string,
  patientId: string
): Promise<ThreadResolution> {
  const candidates = normalizeThreads(
    await repository.getRecentThreads(tenantId, patientId)
  ).reverse()
  return candidates.length
    ? { status: 'choice-required', candidates }
    : { status: 'empty' }
}

export async function resolveThread(
  repository: ConversationRepository,
  tenantId: string,
  conversation: {
    patientId: string
    timestamp: Conversation['timestamp']
    channel: Conversation['channel']
    lastMessageCallId: Conversation['lastMessageCallId']
    lastMessageThreadId: Conversation['lastMessageThreadId']
  }
): Promise<ThreadResolution> {
  try {
    if (conversation.lastMessageThreadId && conversation.channel !== 'Voice') {
      const exact = await validateThread(
        repository,
        tenantId,
        conversation.patientId,
        conversation.lastMessageThreadId
      )
      if (exact) {
        return {
          status: 'resolved',
          threadId: exact.threadId,
          basis: 'exact',
          channel:
            exact.channel === 'Unknown' ? conversation.channel : exact.channel,
        }
      }
    }

    if (conversation.channel === 'Voice') {
      if (conversation.lastMessageCallId && conversation.lastMessageThreadId) {
        const threadId = exactCallThreadId(
          await repository.getExactVoiceCall(
            tenantId,
            conversation.patientId,
            conversation.lastMessageCallId
          )
        )
        if (threadId !== conversation.lastMessageThreadId) {
          return recentChoice(repository, tenantId, conversation.patientId)
        }
        const voiceThread = await validateThread(
          repository,
          tenantId,
          conversation.patientId,
          threadId
        )
        if (voiceThread?.channel === 'Voice') {
          return {
            status: 'resolved',
            threadId,
            basis: 'exact',
            channel: 'Voice',
          }
        }
      }
      return recentChoice(repository, tenantId, conversation.patientId)
    }

    if (
      conversation.channel === 'SMS' ||
      conversation.channel === 'Web Widget'
    ) {
      const rawWindow = await repository.getProducerThreadWindow(
        tenantId,
        conversation.patientId,
        conversation.timestamp
      )
      const rawCount =
        typeof rawWindow === 'object' && rawWindow !== null
          ? Object.keys(rawWindow).length
          : 0
      const candidates = normalizeThreads(rawWindow)
      if (candidates.length !== rawCount) {
        return recentChoice(repository, tenantId, conversation.patientId)
      }
      const searchable =
        rawCount >= THREAD_RESOLUTION_QUERY_SIZE
          ? candidates.slice(0, THREAD_RESOLUTION_QUERY_SIZE - 1)
          : candidates
      const match = searchable.find(
        (thread) =>
          (conversation.channel === 'SMS' && thread.serviceMissing) ||
          thread.channel === conversation.channel
      )
      if (match) {
        return {
          status: 'resolved',
          threadId: match.threadId,
          basis: 'producer-rule',
          channel: conversation.channel,
        }
      }
    }

    return recentChoice(repository, tenantId, conversation.patientId)
  } catch (error) {
    return { status: 'error', kind: errorKind(error) }
  }
}
